import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService, Package, PackageLink, PackageLinkTarget, PackageVersion } from '@/cloud/services/cloud-api.service';
import { MarkdownEditorComponent } from '@/shared/components/markdown-editor/markdown-editor.component';
import { TranslationService } from '@/layout/service/translation.service';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

/** Versions offered in the pin picker per request; typing searches the whole history on the server. */
export const VERSION_OPTION_LIMIT = 50;
const VERSION_SEARCH_DEBOUNCE_MS = 300;
export const MAX_LINK_NOTES_LENGTH = 10000;

export const DOWNLOAD_WINDOW_OPTIONS = [
    { label: '15 min', value: 15 },
    { label: '1 h', value: 60 },
    { label: '6 h', value: 360 },
    { label: '24 h', value: 1440 }
];

/**
 * Share-links panel for a package: lists the managed public links, creates new ones with an
 * explicit pinned-version / always-latest choice (any version in the history can be pinned),
 * edits their label and Markdown notes for the public page, and revokes them.
 */
@Component({
    selector: 'app-package-links',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, TableModule, DialogModule, SelectModule, InputTextModule, DatePickerModule, RadioButtonModule, TagModule, TooltipModule, ProgressSpinnerModule, MessageModule, EmptyStateComponent, MarkdownEditorComponent],
    templateUrl: './package-links.html',
    styleUrl: './package-links.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageLinks {
    private api = inject(CloudApiService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    /** The package whose links are managed. */
    pkg = input.required<Package>();
    /** The version currently marked Latest, if any. */
    latestVersion = input<{ id: string; assemblyVersion: string } | null | undefined>(null);

    links = signal<PackageLink[]>([]);
    loading = signal(false);
    loadError = signal(false);

    showCreateDialog = signal(false);
    target = signal<PackageLinkTarget>('latest');
    pinnedVersion = signal<PackageVersion | null>(null);
    label = signal('');
    notes = signal('');
    expiresAt = signal<Date | null>(null);
    downloadWindow = signal(60);
    creating = signal(false);
    createError = signal(false);
    createdLink = signal<PackageLink | null>(null);

    /** Pin picker options: the newest versions, or the server's matches for the typed search. */
    versionOptions = signal<PackageVersion[]>([]);
    versionsLoading = signal(false);
    private versionRequest = 0;
    private versionSearchTimer: ReturnType<typeof setTimeout> | undefined;

    editingLink = signal<PackageLink | null>(null);
    editLabel = signal('');
    editNotes = signal('');
    saving = signal(false);
    saveError = signal(false);

    readonly maxNotesLength = MAX_LINK_NOTES_LENGTH;
    readonly downloadWindowOptions = DOWNLOAD_WINDOW_OPTIONS;
    readonly minExpiry = new Date();
    readonly canPinSelected = computed(() => !!this.pinnedVersion());
    readonly canUseLatest = computed(() => !!this.latestVersion());
    readonly canCreate = computed(() => !this.creating() && (this.target() === 'latest' ? this.canUseLatest() : this.canPinSelected()));
    readonly createdUrl = computed(() => {
        const link = this.createdLink();
        return link ? this.api.packageLinkUrl(link.id) : '';
    });

    private request = 0;

    constructor() {
        effect(() => {
            const id = this.pkg().id;
            void this.load(id);
        });
        this.destroyRef.onDestroy(() => {
            ++this.request;
            ++this.versionRequest;
            clearTimeout(this.versionSearchTimer);
        });
    }

    async load(packageId = this.pkg().id): Promise<void> {
        const request = ++this.request;
        this.loading.set(true);
        this.loadError.set(false);
        try {
            const links = await this.api.listPackageLinks(packageId);
            if (request !== this.request) return;
            this.links.set(links);
        } catch {
            if (request !== this.request) return;
            this.links.set([]);
            this.loadError.set(true);
        } finally {
            if (request === this.request) this.loading.set(false);
        }
    }

    /** Opens the create dialog. Passing a version preselects "pinned to that version". */
    openCreate(version?: PackageVersion | null): void {
        this.createdLink.set(null);
        this.createError.set(false);
        this.label.set('');
        this.notes.set('');
        this.expiresAt.set(null);
        this.downloadWindow.set(60);
        this.pinnedVersion.set(version ?? null);
        this.versionOptions.set(version ? [version] : []);
        this.target.set(version ? 'pinned' : this.canUseLatest() ? 'latest' : 'pinned');
        this.showCreateDialog.set(true);
        void this.loadVersionOptions('', !version);
    }

    /** Loads pin picker options; with `selectDefault`, preselects the Latest (or newest) version. */
    async loadVersionOptions(search: string, selectDefault = false): Promise<void> {
        const request = ++this.versionRequest;
        this.versionsLoading.set(true);
        try {
            const page = await this.api.getPackageVersions(this.pkg().id, { search, limit: VERSION_OPTION_LIMIT });
            if (request !== this.versionRequest) return;
            const pinned = this.pinnedVersion();
            // Keep the chosen version selectable even when the search does not match it.
            const options = pinned && !page.items.some((v) => v.id === pinned.id) ? [pinned, ...page.items] : page.items;
            this.versionOptions.set(options);
            if (selectDefault && !pinned) {
                const fallback = options.find((v) => v.isLatest) ?? options[0] ?? null;
                this.pinnedVersion.set(fallback);
                if (!fallback && this.target() === 'pinned' && this.canUseLatest()) this.target.set('latest');
            }
        } catch {
            if (request === this.versionRequest && selectDefault && !this.pinnedVersion() && this.canUseLatest()) this.target.set('latest');
        } finally {
            if (request === this.versionRequest) this.versionsLoading.set(false);
        }
    }

    /** Pin picker filter box: searches the whole version history on the server. */
    searchVersions(term: string | null | undefined): void {
        clearTimeout(this.versionSearchTimer);
        this.versionSearchTimer = setTimeout(() => void this.loadVersionOptions((term ?? '').trim()), VERSION_SEARCH_DEBOUNCE_MS);
    }

    closeCreate(): void {
        this.showCreateDialog.set(false);
        this.createdLink.set(null);
    }

    async create(): Promise<void> {
        const pkg = this.pkg();
        if (!this.canCreate()) return;
        const request = this.request;
        this.creating.set(true);
        this.createError.set(false);
        try {
            const link = await this.api.createPackageLink(pkg.id, {
                versionId: this.target() === 'latest' ? null : this.pinnedVersion()!.id,
                label: this.label().trim() || null,
                notes: this.notes().trim() || null,
                expiresUtc: this.expiresAt()?.toISOString() ?? null,
                downloadWindowMinutes: this.downloadWindow()
            });
            if (request !== this.request) return;
            this.links.update((existing) => [link, ...existing]);
            this.createdLink.set(link);
            this.messageService.add({ severity: 'success', summary: this.t()('links.created'), life: 2000 });
        } catch {
            if (request === this.request) this.createError.set(true);
        } finally {
            if (request === this.request) this.creating.set(false);
        }
    }

    openEdit(link: PackageLink): void {
        this.editingLink.set(link);
        this.editLabel.set(link.label ?? '');
        this.editNotes.set(link.notes ?? '');
        this.saveError.set(false);
    }

    closeEdit(): void {
        if (!this.saving()) this.editingLink.set(null);
    }

    async saveEdit(): Promise<void> {
        const link = this.editingLink();
        if (!link || this.saving() || this.editNotes().length > MAX_LINK_NOTES_LENGTH) return;
        const request = this.request;
        this.saving.set(true);
        this.saveError.set(false);
        try {
            const updated = await this.api.updatePackageLink(this.pkg().id, link.id, { label: this.editLabel().trim(), notes: this.editNotes().trim() });
            if (request !== this.request) return;
            this.links.update((existing) => existing.map((item) => (item.id === updated.id ? updated : item)));
            this.editingLink.set(null);
            this.messageService.add({ severity: 'success', summary: this.t()('links.saved'), life: 2000 });
        } catch {
            if (request === this.request) this.saveError.set(true);
        } finally {
            if (request === this.request) this.saving.set(false);
        }
    }

    revoke(link: PackageLink): void {
        const request = this.request;
        this.confirmationService.confirm({
            header: this.t()('links.revoke'),
            message: this.t()('links.revokeConfirm'),
            icon: 'pi pi-exclamation-triangle',
            acceptButtonProps: { severity: 'danger', label: this.t()('links.revoke') },
            rejectButtonProps: { label: this.t()('links.cancel'), text: true },
            accept: () => {
                if (request === this.request) void this.performRevoke(link);
            }
        });
    }

    private async performRevoke(link: PackageLink): Promise<void> {
        const request = this.request;
        try {
            await this.api.revokePackageLink(this.pkg().id, link.id);
            if (request !== this.request) return;
            this.links.update((existing) => existing.filter((item) => item.id !== link.id));
            if (this.createdLink()?.id === link.id) this.createdLink.set(null);
            this.messageService.add({ severity: 'success', summary: this.t()('links.revoked'), life: 2000 });
        } catch {
            if (request === this.request) this.messageService.add({ severity: 'error', summary: this.t()('links.revokeError'), life: 3000 });
        }
    }

    url(link: PackageLink): string {
        return this.api.packageLinkUrl(link.id);
    }

    copy(link: PackageLink): void {
        navigator.clipboard
            .writeText(this.url(link))
            .then(() => this.messageService.add({ severity: 'success', summary: this.t()('links.copied'), life: 2000 }))
            .catch(() => this.messageService.add({ severity: 'error', summary: this.t()('console.copyFailed'), life: 3000 }));
    }

    targetLabel(link: PackageLink): string {
        if (link.target === 'latest') return this.t()('links.latest');
        return link.versionAssemblyVersion ? this.t()('links.pinned').replace('{version}', link.versionAssemblyVersion) : this.t()('links.pinnedUnknown');
    }

    isExpired(link: PackageLink): boolean {
        return !!link.expiresUtc && new Date(link.expiresUtc).getTime() <= Date.now();
    }

    formatDate(value: string | null | undefined): string {
        if (!value) return this.t()('links.never');
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    }

    fill(key: string, values: Record<string, string | number>): string {
        return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, String(value)), this.t()(key));
    }
}
